import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BrandingContext = createContext();

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GYANODAYA_LOGO = 'https://static.prod-images.emergentagent.com/jobs/2d9de157-6aba-4791-b4d7-cfe9f78303cb/images/caa72923253f39d305789ea60b60a4f20b404a1e2c8d1c106a92bfb7211cd8c8.png';

// Default app name and slogan - can be overridden via branding settings
const DEFAULT_APP_NAME = 'Gyanodaya';
const DEFAULT_SLOGAN = 'Rising of Knowledge';

// Local storage key for caching branding
const BRANDING_CACHE_KEY = 'tenant_branding_cache';

// Helper to resolve logo URL (convert relative paths to full URLs)
const resolveLogoUrl = (url) => {
  if (!url) return GYANODAYA_LOGO;
  // If it's already a full URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  // If it's a relative path, prepend the backend URL
  if (url.startsWith('/api/') || url.startsWith('/uploads/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  return url;
};

// Helper to save branding to local storage cache
const saveBrandingToCache = (brandingData) => {
  try {
    localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify({
      ...brandingData,
      cachedAt: Date.now()
    }));
  } catch (e) {
    console.error('Failed to cache branding:', e);
  }
};

// Helper to get branding from local storage cache
const getBrandingFromCache = () => {
  try {
    const cached = localStorage.getItem(BRANDING_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    console.error('Failed to read branding cache:', e);
  }
  return null;
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(() => {
    // Initialize from cache if available for faster initial render
    const cached = getBrandingFromCache();
    if (cached && cached.logo_url) {
      return {
        app_name: cached.app_name || DEFAULT_APP_NAME,
        slogan: cached.slogan || DEFAULT_SLOGAN,
        logo_url: cached.logo_url,
        primary_color: cached.primary_color || '#2563EB',
        portal_url: cached.portal_url || '',
        loading: true, // Still set loading to true to verify with server
      };
    }
    return {
      app_name: DEFAULT_APP_NAME,
      slogan: DEFAULT_SLOGAN,
      logo_url: GYANODAYA_LOGO,
      primary_color: '#2563EB',
      portal_url: '',
      loading: true,
    };
  });
  
  // Track if we've already fetched to avoid duplicate fetches
  const hasFetchedRef = useRef(false);

  // Get site_id from URL for tenant-specific branding on login page
  const getSiteIdFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('site') || urlParams.get('site_id');
  };

  // Initial fetch on mount
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (token) {
      // Fetch authenticated branding (uses user's tenant_id)
      fetchBranding();
    } else {
      // Fetch public branding with site_id from URL (for login page)
      fetchPublicBranding();
    }
  }, []);
  
  // Listen for storage events (token changes) to refresh branding
  useEffect(() => {
    const handleStorageChange = (e) => {
      // If token changed, refresh branding
      if (e.key === 'token') {
        if (e.newValue) {
          // Token added/changed - fetch authenticated branding
          fetchBranding();
        } else {
          // Token removed (logout) - fetch public branding
          fetchPublicBranding();
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    // Apply primary color to CSS variables
    if (branding.primary_color) {
      const root = document.documentElement;
      root.style.setProperty('--brand-primary', branding.primary_color);
      
      // Convert hex to HSL for Tailwind
      const hsl = hexToHSL(branding.primary_color);
      if (hsl) {
        root.style.setProperty('--primary', `${hsl.h} ${hsl.s}% ${hsl.l}%`);
      }
    }
  }, [branding.primary_color]);

  useEffect(() => {
    // Update document title with app name from branding
    document.title = branding.app_name || DEFAULT_APP_NAME;
  }, [branding.app_name]);

  const fetchPublicBranding = async () => {
    try {
      // Get site_id from URL for tenant-specific branding
      const siteId = getSiteIdFromUrl();
      let url = `${API_URL}/settings/branding/public`;
      if (siteId) {
        url += `?site_id=${encodeURIComponent(siteId)}`;
      }
      
      const response = await axios.get(url);
      // Accept any valid logo URL (including uploaded files and external URLs)
      const logoUrl = response.data.logo_url;
      const isValidLogoUrl = logoUrl && (
        logoUrl.startsWith('http://') ||
        logoUrl.startsWith('https://') ||
        logoUrl.startsWith('/api/uploads') ||
        logoUrl.startsWith('data:image/')
      );
      
      const resolvedLogoUrl = isValidLogoUrl ? resolveLogoUrl(logoUrl) : GYANODAYA_LOGO;
      const newBranding = {
        app_name: response.data.app_name || DEFAULT_APP_NAME,
        slogan: response.data.slogan || DEFAULT_SLOGAN,
        logo_url: resolvedLogoUrl,
        primary_color: response.data.primary_color || '#2563EB',
        portal_url: response.data.portal_url || '',
        loading: false,
      };
      
      setBranding(newBranding);
      
      // Cache the branding for persistence across refreshes
      saveBrandingToCache(newBranding);
    } catch (error) {
      console.error('Failed to fetch public branding:', error);
      // On error, try to use cached branding first, then fall back to default
      const cached = getBrandingFromCache();
      setBranding(prev => ({ 
        ...prev, 
        logo_url: cached?.logo_url || GYANODAYA_LOGO,
        primary_color: cached?.primary_color || prev.primary_color,
        loading: false 
      }));
    }
  };

  const fetchBranding = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get(`${API_URL}/settings/branding`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Accept any valid logo URL (including uploaded files and external URLs)
        const logoUrl = response.data.logo_url;
        const isValidLogoUrl = logoUrl && (
          logoUrl.startsWith('http://') ||
          logoUrl.startsWith('https://') ||
          logoUrl.startsWith('/api/uploads') ||
          logoUrl.startsWith('data:image/')
        );
        
        const resolvedLogoUrl = isValidLogoUrl ? resolveLogoUrl(logoUrl) : GYANODAYA_LOGO;
        const newBranding = {
          app_name: response.data.app_name || DEFAULT_APP_NAME,
          slogan: response.data.slogan || DEFAULT_SLOGAN,
          logo_url: resolvedLogoUrl,
          primary_color: response.data.primary_color || '#2563EB',
          portal_url: response.data.portal_url || '',
          loading: false,
        };
        
        setBranding(newBranding);
        
        // Cache the branding for persistence across refreshes
        saveBrandingToCache(newBranding);
      } else {
        // Fallback to public endpoint
        await fetchPublicBranding();
      }
    } catch (error) {
      console.error('Failed to fetch branding:', error);
      // On error, try to use cached branding first, then fall back to default
      const cached = getBrandingFromCache();
      setBranding(prev => ({ 
        ...prev, 
        logo_url: cached?.logo_url || GYANODAYA_LOGO,
        primary_color: cached?.primary_color || prev.primary_color,
        loading: false 
      }));
    }
  };

  const updateBranding = async (newBranding) => {
    try {
      const token = localStorage.getItem('token');
      // Send all branding fields to API
      const dataToSend = {
        app_name: newBranding.app_name || DEFAULT_APP_NAME,
        slogan: newBranding.slogan || DEFAULT_SLOGAN,
        logo_url: newBranding.logo_url,
        primary_color: newBranding.primary_color || '#2563EB',
        portal_url: newBranding.portal_url || '',
      };
      await axios.post(`${API_URL}/settings/branding`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const updatedBranding = { 
        app_name: dataToSend.app_name,
        slogan: dataToSend.slogan,
        logo_url: newBranding.logo_url,
        primary_color: dataToSend.primary_color,
        portal_url: dataToSend.portal_url,
        loading: false,
      };
      
      // Update local state
      setBranding(updatedBranding);
      
      // Cache the updated branding
      saveBrandingToCache(updatedBranding);
      
      return true;
    } catch (error) {
      console.error('Failed to update branding:', error);
      return false;
    }
  };

  const refreshBranding = useCallback(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchBranding();
    } else {
      fetchPublicBranding();
    }
  }, []);

  return (
    <BrandingContext.Provider value={{ branding, updateBranding, refreshBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

// Helper function to convert hex to HSL
function hexToHSL(hex) {
  // Remove #
  hex = hex.replace('#', '');
  
  // Parse RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
      default:
        h = 0;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

export default BrandingContext;
